// src/components/pages/EventManager/organisms/EventForm.js

import React, { useState } from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db, auth } from "../../../../firebase";

import CustomCard from "../../../molecules/ChallengeCreator/CustomCard";
import InputField from "../atoms/InputField";
import DateFields from "../molecules/DateFields";
import CustomButton from "../atoms/CustomButton";
import ErrorMessage from "../atoms/ErrorMessage";

const EventForm = ({ theme, onSuccessfulSubmit }) => {
  // Form fields state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [price, setPrice] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const currentUser = auth.currentUser;
    if (!currentUser) {
      setError("No authenticated user found.");
      return;
    }

    // Get today's date at local midnight
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    if (!title) {
      setError("Please provide a title for the event.");
      return;
    }
    if (!description) {
      setError("Please provide a description for the event.");
      return;
    }
    if (!startDate) {
      setError("Please select a start date for the event.");
      return;
    }

    // Parse startDate ("YYYY-MM-DD") into local Date
    const [startYear, startMonth, startDay] = startDate.split("-").map(Number);
    const start = new Date(startYear, startMonth - 1, startDay);

    if (start < today) {
      setError("The start date cannot be before today.");
      return;
    }
    if (!endDate) {
      setError("Please select an end date for the event.");
      return;
    }

    // Parse endDate ("YYYY-MM-DD") into local Date
    const [endYear, endMonth, endDay] = endDate.split("-").map(Number);
    const end = new Date(endYear, endMonth - 1, endDay);

    if (end <= start) {
      setError("The end date must be after the start date.");
      return;
    }
    if (end - start < 24 * 60 * 60 * 1000) {
      setError("The end date must be at least one day after the start date.");
      return;
    }
    if (!price) {
      setError("Please provide a purchase price for the event.");
      return;
    }

    const priceNum = Number(price);
    if (isNaN(priceNum)) {
      setError("The purchase price must be a valid number.");
      return;
    }

    try {
      await addDoc(collection(db, "events"), {
        title,
        description,
        startDate: start,
        endDate: end,
        price: priceNum,
        createdAt: serverTimestamp(),
        creatorUid: currentUser.uid,
      });

      // Reset form after successful submission
      setTitle("");
      setDescription("");
      setStartDate("");
      setEndDate("");
      setPrice("");
      setError("");

      if (onSuccessfulSubmit) onSuccessfulSubmit();
    } catch (err) {
      setError(`Error creating event: ${err.message}`);
    }
  };

  return (
    <CustomCard title="New Event Form">
      <Box>
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <InputField
              label="Title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
            <InputField
              label="Description"
              type="textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
            />
            <DateFields
              startDate={startDate}
              endDate={endDate}
              onStartChange={(e) => setStartDate(e.target.value)}
              onEndChange={(e) => setEndDate(e.target.value)}
            />
            <InputField
              label="Purchase Price"
              type="text"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              required
              inputMode="numeric"
              pattern="[0-9]*"
              allowAllCharacters={false}
            />
            <CustomButton
              type="submit"
              theme={theme}
              onClick={handleSubmit}
              sx={{
                backgroundColor: theme.colors.green,
                marginTop: "18px",
                borderRadius: "5px",
                "&:hover": {
                  backgroundColor: theme.colors.blue,
                },
              }}
            >
              Create
            </CustomButton>
          </Box>
        </form>
        <ErrorMessage message={error} />
      </Box>
    </CustomCard>
  );
};

EventForm.propTypes = {
  theme: PropTypes.object.isRequired,
  onSuccessfulSubmit: PropTypes.func,
};

export default EventForm;
